import { useEffect, useMemo, useRef, useState } from 'react';

import { RentalManagerPaymentProductType } from '@zg-rentals/ts-rental-manager-api-client';
import {
  useRentalManagerUserFreemiumV2WebApiHandler_startPaymentMethodSession,
  useRentalManagerUserPropertyPaymentWebApiHandler_getDefaultPaymentMethod,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';

import { useCharge } from '../../../hooks/queries/freemium';
import { getCookie } from '../../../utils/cookieUtils';

import { ERROR_CODES, mapPaymentError, mapPaymentFormError, PAYMENT_PHASE } from '../../../constants/PaymentConstants';

import SpinnerContainer from '../../SpinnerContainer';
import CreditCardCapture from '../CreditCardCapture';
import PaymentError from '../PaymentError';
import SelectPaymentMethod from '../SelectPaymentMethod';

import type { ValueOf } from 'type-fest';
import type { FreemiumChargeV2ResponseWebDto } from '@zg-rentals/ts-rental-manager-api-client';
import type { DatarouterError } from '@zg-rentals/web-base';
import type { PAYMENT_METHOD } from '../../../constants/PaymentConstants';
import type { FreemiumDatarouterError } from '../../modals/FreemiumErrorModal';
import type { MetroidCaptureError } from '../AlicornPaymentIframe';

export type Props = {
  listingAlias: string;
  onPaymentMethodChange?: (method: string) => void;
  isRenderedInPageView: boolean;
  setHeadingText: (headingText: string) => void;
  setIsChargeFetching?: (isFetching: boolean) => void;
  onFreemiumChargeSuccess?: (response: FreemiumChargeV2ResponseWebDto) => void;
  onFreemiumChargeError?: (error: FreemiumDatarouterError) => void;
};

const PaymentDetails = ({
  listingAlias,
  onPaymentMethodChange,
  isRenderedInPageView,
  setHeadingText,
  setIsChargeFetching,
  onFreemiumChargeSuccess,
  onFreemiumChargeError,
}: Props) => {
  const hostApp = getCookie('hostApp');

  const [currentPhase, setCurrentPhase] = useState('');
  const [isFormLoaded, setIsFormLoaded] = useState(false);
  const [isCaptured, setIsCaptured] = useState(false);
  const [captureError, setCaptureError] = useState<MetroidCaptureError | null>(null);

  const {
    data: defaultPaymentMethod,
    isSuccess: defaultPaymentMethodSuccess,
    isLoading: isDefaultPaymentMethodLoading,
  } = useRentalManagerUserPropertyPaymentWebApiHandler_getDefaultPaymentMethod({ listingAlias });

  const {
    data: sessionData,
    error: startPaymentMethodSessionError,
    mutate: startPaymentMethodSession,
    isPending: isStartPaymentMethodSessionPending,
  } = useRentalManagerUserFreemiumV2WebApiHandler_startPaymentMethodSession({
    meta: {
      context: {
        api: 'rental-manager',
        details: 'Starting payment method session',
      },
    },
  });

  const {
    refetch: refetchCharge,
    data: chargeData,
    error: chargeError,
  } = useCharge({
    listingAlias,
    productType: RentalManagerPaymentProductType.FREEMIUM_PAID,
    hostApp,
  });

  const combinedError = useMemo(() => {
    if (captureError?.code) {
      return { code: captureError?.code, code_details: captureError?.code_details || undefined };
    }
    const datarouterError = (startPaymentMethodSessionError as DatarouterError) || chargeError;
    if (datarouterError) {
      return { message: datarouterError?.message, code: datarouterError?.code };
    }
    return undefined;
  }, [captureError, chargeError, startPaymentMethodSessionError]);

  const isInMaintenanceMode = combinedError?.code === ERROR_CODES.METROID_MAINTENANCE_MODE;
  const errorMessage = combinedError ? mapPaymentError(combinedError) : '';
  const formErrorMessage = combinedError ? mapPaymentFormError(combinedError) : '';

  useEffect(() => {
    if (combinedError) {
      if (!formErrorMessage) {
        setCurrentPhase(PAYMENT_PHASE.CAUGHT_ERROR);
      }
    } else if (isCaptured) {
      setCurrentPhase(PAYMENT_PHASE.CREDIT_CARD_CHARGE);
    } else if (defaultPaymentMethod) {
      setCurrentPhase(
        defaultPaymentMethod.defaultPaymentMethodExists
          ? PAYMENT_PHASE.SELECT_PAYMENT_METHOD
          : PAYMENT_PHASE.CREDIT_CARD_CAPTURE,
      );
    } else if (defaultPaymentMethodSuccess === false && !isDefaultPaymentMethodLoading) {
      setCurrentPhase(PAYMENT_PHASE.CREDIT_CARD_CAPTURE);
    }
  }, [
    combinedError,
    formErrorMessage,
    isCaptured,
    defaultPaymentMethod,
    defaultPaymentMethodSuccess,
    isDefaultPaymentMethodLoading,
    refetchCharge,
  ]);

  useEffect(() => {
    const needsSession =
      currentPhase === PAYMENT_PHASE.SELECT_PAYMENT_METHOD || currentPhase === PAYMENT_PHASE.CREDIT_CARD_CAPTURE;
    if (needsSession && !sessionData && !startPaymentMethodSessionError && !isStartPaymentMethodSessionPending) {
      startPaymentMethodSession({ postData: { listingAlias, hostApp } });
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- preserving existing behavior; not re-verified
  }, [currentPhase, listingAlias, hostApp, startPaymentMethodSession, sessionData, isStartPaymentMethodSessionPending]);

  useEffect(() => {
    if (currentPhase === PAYMENT_PHASE.CREDIT_CARD_CHARGE && !chargeData && !chargeError) {
      refetchCharge();
    }
  }, [currentPhase, chargeData, chargeError, refetchCharge]);

  useEffect(() => {
    if (currentPhase !== PAYMENT_PHASE.CREDIT_CARD_CHARGE) return;
    const isPaymentValid = Boolean(chargeData?.isPaymentValid);
    setHeadingText(isPaymentValid ? 'Payment successful' : 'Processing your payment…');
  }, [currentPhase, chargeData?.isPaymentValid, setHeadingText]);

  useEffect(() => {
    if (currentPhase === PAYMENT_PHASE.CREDIT_CARD_CHARGE) {
      setIsChargeFetching?.(true);
    }
  }, [currentPhase, setIsChargeFetching]);

  const hasCalledSuccessRef = useRef(false);
  useEffect(() => {
    if (
      currentPhase === PAYMENT_PHASE.CREDIT_CARD_CHARGE &&
      chargeData?.isPaymentValid === true &&
      !hasCalledSuccessRef.current
    ) {
      hasCalledSuccessRef.current = true;
      setIsChargeFetching?.(false);
      onFreemiumChargeSuccess?.(chargeData);
    }
  }, [currentPhase, chargeData, setIsChargeFetching, onFreemiumChargeSuccess]);

  useEffect(() => {
    if (currentPhase === PAYMENT_PHASE.CAUGHT_ERROR && chargeError) {
      onFreemiumChargeError?.(chargeError as FreemiumDatarouterError);
    }
  }, [currentPhase, chargeError, onFreemiumChargeError]);

  const handlePaymentMethodChange = (method: ValueOf<typeof PAYMENT_METHOD>): void => {
    setCurrentPhase(PAYMENT_PHASE.CREDIT_CARD_CAPTURE);
    setIsCaptured(false);
    setCaptureError(null);
    setIsFormLoaded(false);
    onPaymentMethodChange?.(method);
  };

  if (chargeData?.isPaymentValid === false) {
    return <SpinnerContainer />;
  }

  switch (currentPhase) {
    case PAYMENT_PHASE.SELECT_PAYMENT_METHOD:
    case PAYMENT_PHASE.CREDIT_CARD_CAPTURE:
      return (
        <SelectPaymentMethod
          creditCardCapture={
            <CreditCardCapture
              sessionData={sessionData}
              formErrorMessage={formErrorMessage}
              isFormLoaded={isFormLoaded}
              isCaptured={isCaptured}
              onRenderComplete={() => setIsFormLoaded(true)}
              onSubmitSuccess={() => setIsCaptured(true)}
              onSubmitFailure={(err) => setCaptureError(err)}
              useFreemiumValues={true}
            />
          }
          isRenderedInPageView={isRenderedInPageView}
          onPaymentMethodChange={handlePaymentMethodChange}
          setCurrentPhase={setCurrentPhase}
          isFormLoaded={isFormLoaded}
          listingAlias={listingAlias}
        />
      );
    case PAYMENT_PHASE.CREDIT_CARD_CHARGE:
      return <SpinnerContainer />;
    case PAYMENT_PHASE.CAUGHT_ERROR:
      return <PaymentError isInMaintenanceMode={isInMaintenanceMode} errorMessage={errorMessage} />;
    default:
      return <SpinnerContainer />;
  }
};

export default PaymentDetails;
